import { type ReactNode } from 'react';
import { RequiredLabel } from '@gonfalon/ui/RequiredLabel';
import type { TextFieldProps } from '@launchpad-ui/components';
import { Input, TextField as LaunchpadTextField, Text } from '@launchpad-ui/components';

import { FormFieldError } from './FormFieldError';
import {
  type BrowserExtensionControlProps,
  getDisableBrowserExtensionAttributes,
} from './getDisableBrowserExtensionAttributes';
import { useField } from './useField';

import styles from './TextInputField.module.css';

export type FormTextInputFieldProps = TextFieldProps &
  BrowserExtensionControlProps & {
    label?: string | ReactNode;
    description?: ReactNode;
    placeholder?: string;
    updateOnBlur?: boolean;
    variant?: 'default' | 'minimal';
    // Transform the value on every change before it is displayed and committed,
    // e.g. force-uppercase an identifier as the user types.
    format?: (value: string) => string;
    valueAdornment?: ReactNode;
    restoreDefaultOnBlur?: string;
  };

export function TextInputField({
  label,
  placeholder,
  className,
  description,
  isRequired,
  updateOnBlur,
  format,
  valueAdornment,
  restoreDefaultOnBlur,
  disableBrowserExtensions = true,
  ...props
}: FormTextInputFieldProps) {
  const { field, ...fieldProps } = useField<string>({
    updateOnBlur,
    format,
    restoreOnEmptyBlur: restoreDefaultOnBlur,
    ...props,
  });

  const input = (
    <Input
      placeholder={placeholder}
      data-test-id={`${field.name}-input`}
      variant={props.variant}
      {...getDisableBrowserExtensionAttributes(disableBrowserExtensions)}
    />
  );

  return (
    <LaunchpadTextField className={className} name={field.name} {...props} {...fieldProps}>
      {label && <RequiredLabel isRequired={isRequired}>{label}</RequiredLabel>}
      {description && <Text slot="description">{description}</Text>}
      {restoreDefaultOnBlur !== undefined || valueAdornment ? (
        <div className={styles.inputWrapper}>
          {input}
          {valueAdornment && (
            <span className={styles.adornmentOverlay}>
              <span className={styles.ghostValue} aria-hidden>
                {field.state.value}
              </span>
              <span className={styles.adornment}>{valueAdornment}</span>
            </span>
          )}
        </div>
      ) : (
        input
      )}
      <FormFieldError meta={field.state.meta} />
    </LaunchpadTextField>
  );
}
